import { useState } from 'react';
import type { FinancialParams, ChargerConfig, CustomOpexItem, CustomCapexItem } from '../utils/financialDefaults';
import { FINANCIAL_RANGES, FINANCIAL_DEFAULTS } from '../utils/financialDefaults';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Info, Plus, Trash2 } from 'lucide-react';

const C = {
  sec: '#00A3C4', bdr: '#D9E2EC',
  capex: { bg: '#FFF7ED', bdr: '#FED7AA', ico: '🏗️', lbl: '#9A3412' },
  opex: { bg: '#F0F9FF', bdr: '#BFDBFE', ico: '⚙️', lbl: '#1E40AF' },
  revenue: { bg: '#F0FDF4', bdr: '#BBF7D0', ico: '💰', lbl: '#166534' },
  finance: { bg: '#EEF2FF', bdr: '#C7D2FE', ico: '🏦', lbl: '#4338CA' },
  fiscal: { bg: '#FDF2F8', bdr: '#FBCFE8', ico: '🏛️', lbl: '#9D174D' },
};

const SLIDER_HINTS: Partial<Record<keyof FinancialParams, { text: string; url?: string }>> = {
  pvCapex: { text: 'Investering per kWp zonnepanelen incl. montage. Marktrange 2026: €650–€950/kWp.', url: 'https://www.solar.nl' },
  bessCapex: { text: 'Investering per kWh batterijopslag (Li-ion). Marktrange 2026: €250–€450/kWh.', url: 'https://www.lazard.com' },
  backupCapex: { text: 'Noodstroomaggregaat (diesel/gas). Typisch €150–€300/kW.' },
  chargerCapex: { text: 'Kosten per DC-snellader incl. installatie. Typisch €25k–€60k per unit (150–300 kW).', url: 'https://www.elaad.nl' },
  gridConnectionCapex: { text: 'Kosten netaansluiting bij netbeheerder. Sterk afhankelijk van capaciteit en locatie.', url: 'https://www.liander.nl/tarieven' },
  engineeringPct: { text: 'Engineering & projectmanagement als percentage van hardware-CAPEX. Typisch 8–15%.' },
  wacc: { text: 'Gewogen gemiddelde kapitaalkosten.' },
  sdeSubsidy: { text: 'Stimulering Duurzame Energieproductie.', url: 'https://www.rvo.nl/subsidies-financiering/sde' },
  sdeMaxYears: { text: 'Maximale looptijd SDE++-beschikking (standaard 15 jaar).', url: 'https://www.rvo.nl/subsidies-financiering/sde' },
  ereTariff: { text: 'Energie Reductie Eis — vergoeding per groene kWh.', url: 'https://www.rvo.nl/onderwerpen/hbe' },
  co2GridFactor: { text: 'CO₂-uitstoot per kWh uit het Nederlandse elektriciteitsnet.', url: 'https://www.co2emissiefactoren.nl' },
  co2CertPrice: { text: 'Prijs per ton CO₂ op de EU ETS-markt.', url: 'https://www.eex.com' },
  vpbRate: { text: 'Vennootschapsbelasting.', url: 'https://www.belastingdienst.nl' },
  eiaPercentage: { text: 'Geldt voor PV-panelen en batterijopslag (BESS).', url: 'https://www.rvo.nl/subsidies-financiering/eia' },
  miaPercentage: { text: 'Geldt voor laadinfrastructuur (chargers).', url: 'https://www.rvo.nl/subsidies-financiering/mia-vamil' },
  debtRatio: { text: 'Percentage van de investering gefinancierd met vreemd vermogen.' },
  bessReplacementCostReduction: { text: 'Verwachte kostendaling batterijen op moment van vervanging.', url: 'https://www.lazard.com' },
  inflation: { text: 'Jaarlijkse algemene prijsstijging.', url: 'https://www.ecb.europa.eu' },
  blokstroomTariff: { text: 'Tarief voor extra gridcapaciteit tijdens blokstroom-uren (nacht). Vaak lager dan regulier import tarief.' },
  gridTransportTariff: { text: 'Jaarlijks transporttarief per kW gecontracteerd vermogen. Tarief verschilt per netbeheerder en regio.', url: 'https://www.liander.nl/tarieven' },
  gridSystemServiceCost: { text: 'Vaste jaarlijkse kosten voor systeemdienstenverlening door de netbeheerder.' },
  gridMeteringCost: { text: 'Jaarlijkse kosten voor meetdiensten (slimme meter, telemetrie).' },
  locationRentPerM2: { text: 'Huurprijs per vierkante meter per jaar voor de locatie.' },
  locationSizeM2: { text: 'Oppervlakte van de locatie in vierkante meters.' },
};

function FinSlider({ paramKey, value: rawValue, onChange, group, locked, onToggleLock }: {
  paramKey: keyof FinancialParams;
  value: number;
  onChange: (key: keyof FinancialParams, val: number) => void;
  group: 'capex' | 'opex' | 'revenue' | 'finance' | 'fiscal';
  locked?: boolean;
  onToggleLock?: (key: keyof FinancialParams) => void;
}) {
  const range = FINANCIAL_RANGES[paramKey]!;
  const value = rawValue ?? (FINANCIAL_DEFAULTS[paramKey] as number) ?? 0;
  const g = C[group];
  const pct = ((value - range.min) / (range.max - range.min)) * 100;
  const decimals = range.step < 0.1 ? 2 : range.step < 1 ? 1 : 0;
  const formatVal = (v: number) => {
    if (v >= 10000) return `${Math.round(v / 1000).toLocaleString('nl-NL')}k`;
    if (range.step < 0.1) return v.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (range.step < 1) return v.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return v.toLocaleString('nl-NL', { maximumFractionDigits: 0 });
  };

  const [localVal, setLocalVal] = useState(String(value));
  const [editing, setEditing] = useState(false);

  const commitValue = (raw: string) => {
    const parsed = parseFloat(raw.replace(',', '.'));
    if (isNaN(parsed)) { setLocalVal(value.toFixed(decimals)); return; }
    const clamped = Math.min(range.max, Math.max(range.min, parsed));
    onChange(paramKey, clamped);
    setLocalVal(clamped.toFixed(decimals));
  };

  const displayVal = editing ? localVal : value.toFixed(decimals);

  return (
    <div className={`rounded-[10px] px-3.5 py-2 relative ${locked ? 'opacity-60' : ''}`} style={{ background: g.bg, border: `1px solid ${g.bdr}` }}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-[11px] font-semibold flex items-center gap-1" style={{ color: g.lbl }}>
          {onToggleLock && (
            <button onClick={() => onToggleLock(paramKey)} className="cursor-pointer text-[10px] hover:scale-110 transition-transform" title={locked ? 'Ontgrendel' : 'Vergrendel'}>
              {locked ? '🔒' : '🔓'}
            </button>
          )}
          {range.label}
          {SLIDER_HINTS[paramKey] && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="text-[#8899AA] hover:text-[#9D174D] cursor-pointer ml-0.5"><Info size={11} /></button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[240px] text-[10px] leading-relaxed">
                <span>{SLIDER_HINTS[paramKey]!.text}</span>
                {SLIDER_HINTS[paramKey]!.url && (
                  <a href={SLIDER_HINTS[paramKey]!.url} target="_blank" rel="noopener noreferrer" className="block mt-1 text-[9px] underline hover:no-underline" style={{ color: g.lbl }}>Bron →</a>
                )}
              </TooltipContent>
            </Tooltip>
          )}
        </span>
        <div className="flex items-center gap-0.5">
          <input
            type="text"
            inputMode="decimal"
            value={displayVal}
            onFocus={() => { setEditing(true); setLocalVal(value.toFixed(decimals)); }}
            onChange={e => setLocalVal(e.target.value)}
            onBlur={e => { commitValue(e.target.value); setEditing(false); }}
            onKeyDown={e => { if (e.key === 'Enter') { commitValue((e.target as HTMLInputElement).value); setEditing(false); (e.target as HTMLInputElement).blur(); } }}
            disabled={locked}
            className="w-16 text-right text-xs font-bold text-[#0B1F4A] bg-white px-1.5 py-0.5 rounded-md tabular-nums outline-none focus:ring-1 focus:ring-[#00A3C4] disabled:cursor-not-allowed"
            style={{ border: `1px solid ${g.bdr}` }}
          />
          <span className="text-[10px] text-[#8899AA]">{range.unit}</span>
        </div>
      </div>
      <input type="range" min={range.min} max={range.max} step={range.step} value={value}
        onInput={e => !locked && onChange(paramKey, +(e.target as HTMLInputElement).value)}
        onChange={e => !locked && onChange(paramKey, +e.target.value)}
        disabled={locked}
        className="w-full h-1.5 appearance-none rounded-sm cursor-pointer disabled:cursor-not-allowed"
        style={{ background: `linear-gradient(to right, ${C.sec} 0%, ${C.sec} ${pct}%, ${g.bdr} ${pct}%, ${g.bdr} 100%)`, touchAction: 'none' }} />
      <div className="flex justify-between mt-0.5">
        <span className="text-[9px] text-[#8899AA]">{formatVal(range.min)}{range.unit}</span>
        <span className="text-[9px] text-[#8899AA]">{formatVal(range.max)}{range.unit}</span>
      </div>
    </div>
  );
}

const CAPEX_KEYS: (keyof FinancialParams)[] = ['pvCapex', 'bessCapex', 'backupCapex', 'gridConnectionCapex', 'engineeringPct'];
const OPEX_KEYS: (keyof FinancialParams)[] = ['gridImportTariff', 'gridExportTariff', 'blokstroomTariff', 'backupFuelCost', 'omPvPct', 'omBessPerKwh', 'insurancePct', 'inflation', 'energyPriceVolatility', 'gridTransportTariff', 'gridSystemServiceCost', 'gridMeteringCost', 'locationRentPerM2', 'locationSizeM2'];
const REVENUE_KEYS: (keyof FinancialParams)[] = ['chargingTariff', 'ereTariff', 'sdeSubsidy', 'sdeMaxYears', 'tariffEscalation', 'projectDuration', 'wacc', 'co2GridFactor', 'co2CertPrice'];
const FINANCE_KEYS: (keyof FinancialParams)[] = ['debtRatio', 'interestRate', 'loanTerm', 'bessReplacementYear', 'bessReplacementCostReduction'];
const FISCAL_KEYS: (keyof FinancialParams)[] = ['vpbRate', 'eiaPercentage', 'miaPercentage', 'depreciationYears'];

const DEFAULT_CHARGER_ROWS: ChargerConfig[] = [
  { name: 'DC Snellader 150kW', power: 150, count: 2, pricePerUnit: 45000 },
  { name: 'AC Lader 22kW', power: 22, count: 6, pricePerUnit: 8000 },
];

export default function FinancialInputPanel({
  params, onChange, onChargerDetailChange, onCustomOpexChange, onCustomCapexChange, lockedParams, onToggleLock, bessLinked, onToggleBessLink, fiscalEnabled, onToggleFiscal,
}: {
  params: FinancialParams;
  onChange: (key: keyof FinancialParams, value: number) => void;
  onChargerDetailChange?: (config: ChargerConfig[] | undefined) => void;
  onCustomOpexChange?: (items: CustomOpexItem[]) => void;
  onCustomCapexChange?: (items: CustomCapexItem[]) => void;
  lockedParams?: Set<keyof FinancialParams>;
  onToggleLock?: (key: keyof FinancialParams) => void;
  bessLinked?: boolean;
  onToggleBessLink?: () => void;
  fiscalEnabled?: boolean;
  onToggleFiscal?: () => void;
}) {
  const [chargerDetailMode, setChargerDetailMode] = useState(!!params.chargerDetailConfig);
  const [chargerRows, setChargerRows] = useState<ChargerConfig[]>(params.chargerDetailConfig || DEFAULT_CHARGER_ROWS);
  const [customOpexRows, setCustomOpexRows] = useState<CustomOpexItem[]>(params.customOpexItems ?? []);
  const [customCapexRows, setCustomCapexRows] = useState<CustomCapexItem[]>(params.customCapexItems ?? []);

  const toggleChargerDetail = (on: boolean) => { setChargerDetailMode(on); onChargerDetailChange?.(on ? chargerRows : undefined); };
  const updateChargerRow = (idx: number, field: keyof ChargerConfig, value: string | number) => {
    const updated = chargerRows.map((r, i) => i === idx ? { ...r, [field]: value } : r);
    setChargerRows(updated);
    if (chargerDetailMode) onChargerDetailChange?.(updated);
  };
  const addChargerRow = () => { const updated = [...chargerRows, { name: 'Nieuwe lader', power: 50, count: 1, pricePerUnit: 15000 }]; setChargerRows(updated); if (chargerDetailMode) onChargerDetailChange?.(updated); };
  const removeChargerRow = (idx: number) => { const updated = chargerRows.filter((_, i) => i !== idx); setChargerRows(updated); if (chargerDetailMode) onChargerDetailChange?.(updated); };
  const chargerDetailTotal = chargerRows.reduce((s, r) => s + r.count * r.pricePerUnit, 0);

  const handleLinkedChange = (key: keyof FinancialParams, value: number) => {
    onChange(key, value);
    if (bessLinked && key === 'bessCapex') {
      const ratio = value / FINANCIAL_DEFAULTS.bessCapex;
      const newReduction = Math.min(60, Math.max(0, FINANCIAL_DEFAULTS.bessReplacementCostReduction * ratio));
      onChange('bessReplacementCostReduction', Math.round(newReduction / 5) * 5);
    }
  };

  const SliderGrid = ({ keys, group }: { keys: (keyof FinancialParams)[]; group: 'capex' | 'opex' | 'revenue' | 'finance' | 'fiscal' }) => (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
      {keys.map(k => <FinSlider key={k} paramKey={k} value={params[k] as number} onChange={handleLinkedChange} group={group} locked={lockedParams?.has(k)} onToggleLock={onToggleLock} />)}
    </div>
  );

  return (
    <TooltipProvider>
      <div className="bg-white rounded-xl border border-[#D9E2EC] shadow-sm mb-4">
        <div className="px-4 pt-3 pb-1">
          <div className="text-xs font-bold text-[#0B1F4A] mb-2">⚙️ Financiële Parameters</div>
        </div>
        <Tabs defaultValue="capex" className="px-4 pb-4">
          <TabsList className="w-full justify-start bg-[#F5F8FB] border border-[#D9E2EC] rounded-lg h-auto flex-wrap gap-0.5 p-1">
            <TabsTrigger value="capex" className="text-[10px] font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md px-3 py-1.5">🏗️ CAPEX</TabsTrigger>
            <TabsTrigger value="opex" className="text-[10px] font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md px-3 py-1.5">⚙️ OPEX</TabsTrigger>
            <TabsTrigger value="revenue" className="text-[10px] font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md px-3 py-1.5">💰 Opbrengsten</TabsTrigger>
            <TabsTrigger value="finance" className="text-[10px] font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md px-3 py-1.5">🏦 Financiering</TabsTrigger>
            <TabsTrigger value="fiscal" className="text-[10px] font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md px-3 py-1.5">🏛️ Fiscaal</TabsTrigger>
          </TabsList>

          <TabsContent value="capex" className="mt-3">
            <SliderGrid keys={CAPEX_KEYS} group="capex" />
            <div className="mt-2 rounded-lg border border-[#FED7AA] bg-[#FFF7ED] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-[#9A3412]">⚡ Laadinfra configuratie</span>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] text-[#8899AA]">{chargerDetailMode ? 'Detail' : 'Simpel'}</span>
                  <Switch checked={chargerDetailMode} onCheckedChange={toggleChargerDetail} className="data-[state=checked]:bg-[#9A3412] scale-75" />
                </div>
              </div>
              {!chargerDetailMode && <FinSlider paramKey="chargerCapex" value={params.chargerCapex as number} onChange={handleLinkedChange} group="capex" locked={lockedParams?.has('chargerCapex')} onToggleLock={onToggleLock} />}
              {chargerDetailMode && (
                <div className="space-y-1.5">
                  <div className="grid grid-cols-[1fr_70px_55px_80px_70px_28px] gap-1 text-[9px] font-semibold text-[#8899AA] px-1">
                    <span>Type</span><span>kW</span><span>Aantal</span><span>€/stuk</span><span>Subtotaal</span><span></span>
                  </div>
                  {chargerRows.map((row, i) => (
                    <div key={i} className="grid grid-cols-[1fr_70px_55px_80px_70px_28px] gap-1 items-center">
                      <input className="text-[10px] px-1.5 py-1 rounded border border-[#FED7AA] bg-white" value={row.name} onChange={e => updateChargerRow(i, 'name', e.target.value)} />
                      <input type="number" className="text-[10px] px-1.5 py-1 rounded border border-[#FED7AA] bg-white tabular-nums" value={row.power} onChange={e => updateChargerRow(i, 'power', +e.target.value)} />
                      <input type="number" className="text-[10px] px-1.5 py-1 rounded border border-[#FED7AA] bg-white tabular-nums" value={row.count} onChange={e => updateChargerRow(i, 'count', +e.target.value)} min={0} />
                      <input type="number" className="text-[10px] px-1.5 py-1 rounded border border-[#FED7AA] bg-white tabular-nums" value={row.pricePerUnit} onChange={e => updateChargerRow(i, 'pricePerUnit', +e.target.value)} step={1000} />
                      <span className="text-[10px] font-semibold text-[#9A3412] tabular-nums px-1">€{(row.count * row.pricePerUnit / 1000).toFixed(0)}k</span>
                      <button onClick={() => removeChargerRow(i)} className="text-[#8899AA] hover:text-red-500 cursor-pointer"><Trash2 size={12} /></button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-[#FED7AA]">
                    <button onClick={addChargerRow} className="text-[10px] text-[#9A3412] font-semibold flex items-center gap-1 hover:underline cursor-pointer"><Plus size={12} /> Rij toevoegen</button>
                    <span className="text-[11px] font-bold text-[#9A3412]">Totaal: €{(chargerDetailTotal / 1000).toFixed(0)}k</span>
                  </div>
                </div>
              )}
            </div>
            {/* Custom CAPEX items */}
            <div className="mt-3 rounded-lg border border-[#FED7AA] bg-[#FFF7ED] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-[#9A3412]">📋 Overige eenmalige kosten</span>
              </div>
              {customCapexRows.length > 0 && (
                <div className="space-y-1.5 mb-2">
                  <div className="grid grid-cols-[1fr_100px_28px] gap-1 text-[9px] font-semibold text-[#8899AA] px-1">
                    <span>Omschrijving</span><span>Bedrag (€)</span><span></span>
                  </div>
                  {customCapexRows.map((row, i) => (
                    <div key={row.id} className="grid grid-cols-[1fr_100px_28px] gap-1 items-center">
                      <input className="text-[10px] px-1.5 py-1 rounded border border-[#FED7AA] bg-white" value={row.name}
                        onChange={e => {
                          const updated = customCapexRows.map((r, j) => j === i ? { ...r, name: e.target.value } : r);
                          setCustomCapexRows(updated);
                          onCustomCapexChange?.(updated);
                        }} />
                      <input type="number" className="text-[10px] px-1.5 py-1 rounded border border-[#FED7AA] bg-white tabular-nums" value={row.amount} step={1000} min={0}
                        onChange={e => {
                          const updated = customCapexRows.map((r, j) => j === i ? { ...r, amount: +e.target.value } : r);
                          setCustomCapexRows(updated);
                          onCustomCapexChange?.(updated);
                        }} />
                      <button onClick={() => {
                        const updated = customCapexRows.filter((_, j) => j !== i);
                        setCustomCapexRows(updated);
                        onCustomCapexChange?.(updated);
                      }} className="text-[#8899AA] hover:text-red-500 cursor-pointer"><Trash2 size={12} /></button>
                    </div>
                  ))}
                  <div className="text-right text-[11px] font-bold text-[#9A3412] pr-10">
                    Totaal: €{customCapexRows.reduce((s, r) => s + r.amount, 0).toLocaleString('nl-NL')}
                  </div>
                </div>
              )}
              <button onClick={() => {
                const updated = [...customCapexRows, { id: crypto.randomUUID(), name: 'Nieuwe kostenpost', amount: 0 }];
                setCustomCapexRows(updated);
                onCustomCapexChange?.(updated);
              }} className="text-[10px] text-[#9A3412] font-semibold flex items-center gap-1 hover:underline cursor-pointer">
                <Plus size={12} /> Kostenpost toevoegen
              </button>
            </div>
          </TabsContent>
          <TabsContent value="opex" className="mt-3">
            <SliderGrid keys={OPEX_KEYS} group="opex" />
            {/* Netbeheerdertarieven totaal */}
            {((params.gridTransportTariff ?? 0) > 0 || (params.gridSystemServiceCost ?? 0) > 0 || (params.gridMeteringCost ?? 0) > 0) && (
              <div className="mt-2 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] p-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-[#1E40AF]">🔌 Totaal netbeheerdertarieven</span>
                  <span className="text-[12px] font-bold text-[#1E40AF]">
                    Afhankelijk van gMax — zie business case
                  </span>
                </div>
                <p className="text-[9px] text-[#8899AA] mt-1">Transportkosten worden berekend op basis van gecontracteerd vermogen (gMax × transporttarief) + vaste kosten.</p>
              </div>
            )}
            {/* Locatiehuur totaal */}
            {((params.locationRentPerM2 ?? 0) > 0 || (params.locationSizeM2 ?? 0) > 0) && (
              <div className="mt-2 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] p-2.5 flex items-center justify-between">
                <span className="text-[11px] font-semibold text-[#1E40AF]">📐 Totaal huur locatie</span>
                <span className="text-[12px] font-bold text-[#1E40AF]">
                  €{((params.locationRentPerM2 ?? 0) * (params.locationSizeM2 ?? 0)).toLocaleString('nl-NL', { maximumFractionDigits: 0 })}/jaar
                </span>
              </div>
            )}
            {/* Custom OPEX items */}
            <div className="mt-3 rounded-lg border border-[#BFDBFE] bg-[#F0F9FF] p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-[#1E40AF]">📋 Overige kosten</span>
              </div>
              {customOpexRows.length > 0 && (
                <div className="space-y-1.5 mb-2">
                  <div className="grid grid-cols-[1fr_100px_28px] gap-1 text-[9px] font-semibold text-[#8899AA] px-1">
                    <span>Omschrijving</span><span>€/jaar</span><span></span>
                  </div>
                  {customOpexRows.map((row, i) => (
                    <div key={row.id} className="grid grid-cols-[1fr_100px_28px] gap-1 items-center">
                      <input className="text-[10px] px-1.5 py-1 rounded border border-[#BFDBFE] bg-white" value={row.name}
                        onChange={e => {
                          const updated = customOpexRows.map((r, j) => j === i ? { ...r, name: e.target.value } : r);
                          setCustomOpexRows(updated);
                          onCustomOpexChange?.(updated);
                        }} />
                      <input type="number" className="text-[10px] px-1.5 py-1 rounded border border-[#BFDBFE] bg-white tabular-nums" value={row.annualCost} step={500} min={0}
                        onChange={e => {
                          const updated = customOpexRows.map((r, j) => j === i ? { ...r, annualCost: +e.target.value } : r);
                          setCustomOpexRows(updated);
                          onCustomOpexChange?.(updated);
                        }} />
                      <button onClick={() => {
                        const updated = customOpexRows.filter((_, j) => j !== i);
                        setCustomOpexRows(updated);
                        onCustomOpexChange?.(updated);
                      }} className="text-[#8899AA] hover:text-red-500 cursor-pointer"><Trash2 size={12} /></button>
                    </div>
                  ))}
                  <div className="text-right text-[11px] font-bold text-[#1E40AF] pr-10">
                    Totaal: €{customOpexRows.reduce((s, r) => s + r.annualCost, 0).toLocaleString('nl-NL')}/jaar
                  </div>
                </div>
              )}
              <button onClick={() => {
                const updated = [...customOpexRows, { id: crypto.randomUUID(), name: 'Nieuwe kostenpost', annualCost: 0 }];
                setCustomOpexRows(updated);
                onCustomOpexChange?.(updated);
              }} className="text-[10px] text-[#1E40AF] font-semibold flex items-center gap-1 hover:underline cursor-pointer">
                <Plus size={12} /> Kostenpost toevoegen
              </button>
            </div>
          </TabsContent>
          <TabsContent value="revenue" className="mt-3"><SliderGrid keys={REVENUE_KEYS} group="revenue" /></TabsContent>
          <TabsContent value="finance" className="mt-3">
            <div className="mb-3 flex items-center gap-3 p-2.5 rounded-lg bg-[#EEF2FF] border border-[#C7D2FE]">
              {onToggleBessLink && (
                <button onClick={onToggleBessLink} className={`text-[10px] px-2.5 py-1 rounded-md border cursor-pointer transition-colors font-semibold ${bessLinked ? 'bg-[#DBEAFE] border-[#93C5FD] text-[#1D4ED8]' : 'bg-white border-[#D9E2EC] text-[#8899AA]'}`}>
                  {bessLinked ? '🔗 BESS gelinkt' : '⛓️‍💥 BESS los'}
                </button>
              )}
              <span className="text-[9px] text-[#8899AA]">{bessLinked ? 'Vervangingskosten volgen CAPEX-ratio' : 'Onafhankelijk instelbaar'}</span>
            </div>
            <SliderGrid keys={FINANCE_KEYS} group="finance" />
          </TabsContent>
          <TabsContent value="fiscal" className="mt-3">
            <div className="mb-3 flex items-center gap-3 p-2.5 rounded-lg bg-[#FDF2F8] border border-[#FBCFE8]">
              <Switch checked={fiscalEnabled ?? false} onCheckedChange={onToggleFiscal} className="data-[state=checked]:bg-[#9D174D] scale-75" />
              <span className="text-[10px] font-semibold text-[#9D174D]">{fiscalEnabled ? '🏛️ Fiscaal model actief' : '🏛️ Fiscaal model uit'}</span>
            </div>
            <SliderGrid keys={FISCAL_KEYS} group="fiscal" />
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
}
